export const DEFAULT_WEBRISK_ENDPOINT_URL = 'https://webrisk.googleapis.com/v1/uris:search';
export const DEFAULT_WEBRISK_THREAT_TYPES = [
    'MALWARE',
    'SOCIAL_ENGINEERING',
    'UNWANTED_SOFTWARE',
];
export const WEBRISK_UNAVAILABLE_MESSAGE = 'Link safety check unavailable, try again';
/**
 * Fetches an OAuth 2.0 Bearer access token from the GCP Metadata Server
 * (Application Default Credentials on Cloud Run / Cloud Functions). Never logs the token.
 */
export async function fetchMetadataWebRiskAccessToken(fetchImpl = fetch) {
    try {
        const res = await fetchImpl('http://metadata.google.internal/computeMetadata/v1/instance/service-accounts/default/token', { headers: { 'Metadata-Flavor': 'Google' } });
        if (!res.ok)
            return null;
        const body = (await res.json());
        return typeof body.access_token === 'string' && body.access_token.trim().length > 0
            ? body.access_token.trim()
            : null;
    }
    catch {
        return null;
    }
}
function defaultLogWebRiskWarning(reason, context) {
    const entry = {
        severity: 'WARNING',
        service: 'validation',
        message: `Google Web Risk check unverified (fail closed): ${reason}`,
        reason,
        ...(context !== undefined ? { context } : {}),
        timestamp: new Date().toISOString(),
    };
    process.stderr.write(`${JSON.stringify(entry)}\n`);
}
/**
 * Extracts all unique candidate destination URLs from an identifier, qualifier, recall, or workspace
 * payload so every saved destination URL can be checked against the Google Web Risk API (Spec v2.1 §20).
 */
export function collectCandidateDestinationUrls(params) {
    const unique = new Set();
    const addIfNonEmpty = (raw) => {
        if (typeof raw !== 'string')
            return;
        const trimmed = raw.trim();
        if (trimmed.length > 0 && /^https?:\/\//i.test(trimmed)) {
            unique.add(trimmed);
        }
    };
    addIfNonEmpty(params.defaultUrl);
    addIfNonEmpty(params.overrideUrl);
    for (const link of params.links ?? []) {
        if (link && link.removed !== true) {
            addIfNonEmpty(link.href);
        }
    }
    for (const rule of params.rules ?? []) {
        if (rule) {
            addIfNonEmpty(rule.targetHref);
        }
    }
    for (const extra of params.extraUrls ?? []) {
        addIfNonEmpty(extra);
    }
    return [...unique];
}
/**
 * Creates a Google Web Risk v1 (`uris:search`) client that checks destination URLs against
 * `MALWARE`, `SOCIAL_ENGINEERING`, and `UNWANTED_SOFTWARE` threat lists using the service account's
 * Application Default Credentials OAuth 2.0 Bearer token (Spec v2.1 §20).
 *
 * Fails closed: missing access token, network/fetch error, non-2xx HTTP status, or malformed JSON
 * logs the failure reason (without any credential/token) and returns `status: 'unverified'`.
 */
export function createHttpWebRiskClient(options = {}) {
    const endpointUrl = (options.endpointUrl ?? DEFAULT_WEBRISK_ENDPOINT_URL).trim();
    const fetchImpl = options.fetchFn ?? fetch;
    const threatTypes = options.threatTypes && options.threatTypes.length > 0
        ? options.threatTypes
        : DEFAULT_WEBRISK_THREAT_TYPES;
    const tokenProvider = options.getAccessToken ?? (() => fetchMetadataWebRiskAccessToken(fetchImpl));
    const logWarning = options.logWarning ?? defaultLogWebRiskWarning;
    return {
        async checkUris(uris) {
            const normalizedUris = [
                ...new Set(uris
                    .map((u) => (typeof u === 'string' ? u.trim() : ''))
                    .filter((u) => u.length > 0 && /^https?:\/\//i.test(u))),
            ];
            if (normalizedUris.length === 0) {
                return {
                    status: 'safe',
                    unsafeUris: new Map(),
                    blockedHosts: new Set(),
                };
            }
            let accessToken = null;
            try {
                const resolved = await tokenProvider();
                accessToken =
                    typeof resolved === 'string' && resolved.trim().length > 0 ? resolved.trim() : null;
            }
            catch {
                accessToken = null;
            }
            if (accessToken === null) {
                const reason = 'Missing or unavailable OAuth access token for Google Web Risk API';
                logWarning(reason, { uriCount: normalizedUris.length });
                return {
                    status: 'unverified',
                    unsafeUris: new Map(),
                    blockedHosts: new Set(),
                    unverifiedReason: reason,
                };
            }
            const unsafeUris = new Map();
            const blockedHosts = new Set();
            for (const uri of normalizedUris) {
                const queryParams = new URLSearchParams();
                queryParams.set('uri', uri);
                for (const threatType of threatTypes) {
                    queryParams.append('threatTypes', threatType);
                }
                const requestUrl = `${endpointUrl}?${queryParams.toString()}`;
                const quotaProject = options.quotaProjectId?.trim();
                let res;
                try {
                    res = await fetchImpl(requestUrl, {
                        method: 'GET',
                        headers: {
                            Accept: 'application/json',
                            Authorization: `Bearer ${accessToken}`,
                            ...(quotaProject && quotaProject.length > 0
                                ? { 'X-Goog-User-Project': quotaProject }
                                : {}),
                        },
                    });
                }
                catch (err) {
                    const errMsg = err instanceof Error ? err.message : 'fetch failed';
                    const reason = `Google Web Risk fetch error: ${errMsg}`;
                    logWarning(reason, { uri });
                    return {
                        status: 'unverified',
                        unsafeUris: new Map(),
                        blockedHosts: new Set(),
                        unverifiedReason: reason,
                    };
                }
                if (!res.ok) {
                    const reason = `Google Web Risk returned non-2xx HTTP status ${String(res.status)}`;
                    logWarning(reason, { uri, httpStatus: res.status });
                    return {
                        status: 'unverified',
                        unsafeUris: new Map(),
                        blockedHosts: new Set(),
                        unverifiedReason: reason,
                    };
                }
                let rawPayload;
                try {
                    rawPayload = await res.json();
                }
                catch {
                    const reason = 'Google Web Risk returned invalid JSON payload';
                    logWarning(reason, { uri });
                    return {
                        status: 'unverified',
                        unsafeUris: new Map(),
                        blockedHosts: new Set(),
                        unverifiedReason: reason,
                    };
                }
                if (typeof rawPayload !== 'object' || rawPayload === null || Array.isArray(rawPayload)) {
                    const reason = 'Google Web Risk returned non-object JSON payload';
                    logWarning(reason, { uri });
                    return {
                        status: 'unverified',
                        unsafeUris: new Map(),
                        blockedHosts: new Set(),
                        unverifiedReason: reason,
                    };
                }
                const payload = rawPayload;
                const matchedThreats = payload.threat?.threatTypes?.filter((t) => typeof t === 'string' && t.trim().length > 0);
                if (matchedThreats && matchedThreats.length > 0) {
                    unsafeUris.set(uri, matchedThreats);
                    try {
                        const parsedUrl = new URL(uri);
                        const host = parsedUrl.hostname.toLowerCase().replace(/\.$/, '');
                        if (host.length > 0) {
                            blockedHosts.add(host);
                        }
                    }
                    catch {
                        // Ignore URL parse error; URI is already recorded in unsafeUris
                    }
                }
            }
            return {
                status: unsafeUris.size > 0 || blockedHosts.size > 0 ? 'unsafe' : 'safe',
                unsafeUris,
                blockedHosts,
            };
        },
    };
}
/**
 * Creates a mock Google Web Risk client for tests (Spec v2.1 §20).
 * Simulates the Google Web Risk `v1/uris:search` endpoint using `createHttpWebRiskClient`
 * with a custom `fetchFn` and a mock OAuth 2.0 Bearer access token.
 *
 * @param unsafeUrlsOrHostsOrPredicate - Set/array of unsafe URLs or hostnames, a map of URL/host -> threatTypes,
 *   or a predicate `(uri: string) => boolean | readonly string[]`.
 */
export function createMockWebRiskClient(unsafeUrlsOrHostsOrPredicate, options) {
    const evaluateUri = (uri) => {
        const trimmed = uri.trim();
        let host = '';
        try {
            host = new URL(trimmed).hostname.toLowerCase().replace(/\.$/, '');
        }
        catch {
            host = '';
        }
        if (typeof unsafeUrlsOrHostsOrPredicate === 'function') {
            const res = unsafeUrlsOrHostsOrPredicate(trimmed);
            if (Array.isArray(res))
                return res;
            return res === true ? ['MALWARE', 'SOCIAL_ENGINEERING'] : [];
        }
        if (unsafeUrlsOrHostsOrPredicate instanceof Set) {
            const set = unsafeUrlsOrHostsOrPredicate;
            if (set.has(trimmed) || (host.length > 0 && set.has(host))) {
                return ['SOCIAL_ENGINEERING', 'MALWARE'];
            }
            return [];
        }
        if (Array.isArray(unsafeUrlsOrHostsOrPredicate)) {
            const list = unsafeUrlsOrHostsOrPredicate;
            const matched = list.some((entry) => entry.trim() === trimmed || (host.length > 0 && entry.trim().toLowerCase() === host));
            return matched ? ['SOCIAL_ENGINEERING', 'MALWARE'] : [];
        }
        if (typeof unsafeUrlsOrHostsOrPredicate === 'object' && unsafeUrlsOrHostsOrPredicate !== null) {
            const map = unsafeUrlsOrHostsOrPredicate;
            const direct = map[trimmed] ?? (host.length > 0 ? map[host] : undefined);
            if (direct && direct.length > 0) {
                return direct;
            }
        }
        return [];
    };
    return createHttpWebRiskClient({
        getAccessToken: () => options?.accessToken !== undefined ? options.accessToken : 'mock-webrisk-oauth-access-token',
        ...(options?.logWarning !== undefined ? { logWarning: options.logWarning } : {}),
        fetchFn: (inputUrl) => {
            const urlStr = typeof inputUrl === 'string' ? inputUrl : inputUrl.toString();
            const parsed = new URL(urlStr);
            const targetUri = parsed.searchParams.get('uri') ?? '';
            options?.onCheckUri?.(targetUri, urlStr);
            const threats = evaluateUri(targetUri);
            const body = threats.length > 0
                ? {
                    threat: {
                        threatTypes: threats,
                        expireTime: '2099-01-01T00:00:00Z',
                    },
                }
                : {};
            return Promise.resolve(new Response(JSON.stringify(body), {
                status: 200,
                headers: { 'Content-Type': 'application/json' },
            }));
        },
    });
}
//# sourceMappingURL=web-risk-client.js.map